import { useQuery, useQueryClient, UseQueryResult } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { jwtDecode } from "jwt-decode";
import { ReactNode, useCallback, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";

import { InsightsContextValue } from "./InsightsContext";
import type { InsightsContext } from "../types/InsightsContext";

import { instance } from "@/lib/axios/instance";
import { useAuth } from "@/lib/hooks/useAuth";
import { usePermissionMatrix } from "@/lib/hooks/usePermissionMatrix";
import { UserRole } from "@/types/user/userType";

// Query keys for insights context
const insightsQueryKeys = {
  all: ["insightsContext"] as const,
  context: (tenantId?: string, clientId?: string) =>
    ["insightsContext", tenantId, clientId] as const,
};

// Refetch the token this many seconds before its actual `exp`. Gives the backend
// and embed round-trip time to complete before the current token would fail.
const REFETCH_BUFFER_SECONDS = 120;

// On tab return, refetch if the token has less than this much life left (covers
// cases where setTimeout was throttled past its scheduled refetch point).
const VISIBILITY_REFRESH_THRESHOLD_SECONDS = 300;

function getSecondsUntilExpiry(token: string): number | null {
  try {
    const { exp } = jwtDecode<{ exp?: number }>(token);
    if (typeof exp !== "number") return null;
    return exp - Math.floor(Date.now() / 1000);
  } catch {
    return null;
  }
}

export const InsightsProvider = ({ children }: { children: ReactNode }) => {
  const { tenantId, role } = useAuth();
  const { clientId } = useParams();
  const { canView } = usePermissionMatrix();
  const queryClient = useQueryClient();

  // Subscribe to mutation cache to invalidate insights when PowerBI module/integration config changes or is disabled
  useEffect(() => {
    const unsubscribe = queryClient.getMutationCache().subscribe((event) => {
      if (event.type === "updated" && event.mutation?.state.status === "success") {
        const vars = event.mutation.state.variables as {
          integrationName?: string;
          moduleName?: string;
          isActive?: boolean;
        };

        const isPowerBI = vars?.integrationName?.toLowerCase() === "powerbi";
        const isInsightsModule = vars?.moduleName?.toLowerCase() === "insights";
        const isIntegrationDisabled = isPowerBI && vars?.isActive === false && !vars?.moduleName;

        // Invalidate when:
        // 1. PowerBI insights module config changes (including disable)
        // 2. PowerBI integration itself is disabled (no moduleName = integration-level mutation)
        if ((isPowerBI && isInsightsModule) || isIntegrationDisabled) {
          queryClient.invalidateQueries({ queryKey: insightsQueryKeys.all });
        }
      }
    });
    return unsubscribe;
  }, [queryClient]);

  const enableQueryCriteria = (role === UserRole.SUPER_ADMIN && !!tenantId)
                              || (role !== UserRole.SUPER_ADMIN && canView('insights'))

  const {
    data: insightsContext,
    isLoading,
  }: UseQueryResult<InsightsContext, AxiosError> = useQuery({
    queryKey: insightsQueryKeys.context(tenantId ?? undefined, clientId),
    queryFn: () => {
      const params = new URLSearchParams();
      if (tenantId) params.append("selectedTenantId", tenantId);
      if (clientId) params.append("selectedClientId", clientId);
      return instance
        .get<InsightsContext>(`/api/v2/PowerBI/Context?${params.toString()}`)
        .then((res) => res.data);
    },
    refetchOnWindowFocus: false,
    enabled: enableQueryCriteria,
  });

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: insightsQueryKeys.all });
  }, [queryClient]);

  // Proactive refresh: schedule a refetch REFETCH_BUFFER_SECONDS before the
  // token's actual `exp` claim. Uses real expiry, not a guessed interval.
  useEffect(() => {
    const token = insightsContext?.embedToken;
    if (!token) return;

    const secondsUntilExpiry = getSecondsUntilExpiry(token);
    if (secondsUntilExpiry === null) return;

    const msUntilRefresh = Math.max(0, (secondsUntilExpiry - REFETCH_BUFFER_SECONDS) * 1000);
    const timer = window.setTimeout(invalidate, msUntilRefresh);
    return () => window.clearTimeout(timer);
  }, [insightsContext?.embedToken, invalidate]);

  // Reactive refresh: setTimeout can be throttled or frozen while the tab is
  // backgrounded, the window minimized, or the laptop asleep. When the user
  // returns, check expiry explicitly and refetch if the token is close to / past
  // expiry. `visibilitychange` covers backgrounding / minimization; `focus`
  // covers OS-level wake where visibility never actually flipped.
  // Uses a ref so the listeners register once and always read the latest token.
  const embedTokenRef = useRef<string | undefined>(insightsContext?.embedToken);
  embedTokenRef.current = insightsContext?.embedToken;

  useEffect(() => {
    const refetchIfStale = () => {
      const token = embedTokenRef.current;
      if (!token) return;

      const secondsUntilExpiry = getSecondsUntilExpiry(token);
      if (secondsUntilExpiry !== null && secondsUntilExpiry < VISIBILITY_REFRESH_THRESHOLD_SECONDS) {
        invalidate();
      }
    };

    const onVisibilityChange = () => {
      if (document.hidden) return;
      refetchIfStale();
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", refetchIfStale);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", refetchIfStale);
    };
  }, [invalidate]);

  const value = {
    insightsContext,
    isLoading,
  }

  return (
    <InsightsContextValue.Provider
      value={value}
    >
      {children}
    </InsightsContextValue.Provider>
  );
};
